/**
 * CheckInProgress
 *
 * Step indicator for the 3-question guided check-in.
 *
 * The check-in auto-finalizes on the 3rd user turn, so progress is derived from
 * how many answers the user has given — there is no separate progress field to
 * read, and inventing one would let the bar disagree with the conversation.
 *
 * Segments show three states: answered, current, and upcoming. The heading text
 * carries the same information for screen readers and for anyone who cannot
 * distinguish the segment colours.
 */

import { Check } from "lucide-react";

export const TOTAL_QUESTIONS = 3;

interface CheckInProgressProps {
  /** Number of questions the user has answered. */
  answered: number;
  /** True once the conversation is done and only the summary remains. */
  isComplete?: boolean;
  className?: string;
}

export function CheckInProgress({
  answered,
  isComplete = false,
  className = "",
}: CheckInProgressProps) {
  const clamped = Math.min(Math.max(answered, 0), TOTAL_QUESTIONS);
  // While answering, the current question is the next unanswered one.
  const current = Math.min(clamped + 1, TOTAL_QUESTIONS);

  const label = isComplete
    ? "All questions answered"
    : `Question ${current} of ${TOTAL_QUESTIONS}`;

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-foreground">{label}</span>
        {isComplete && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-300">
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            Complete
          </span>
        )}
      </div>

      <div
        className="mt-1.5 flex gap-1.5"
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={TOTAL_QUESTIONS}
        aria-label={label}
      >
        {Array.from({ length: TOTAL_QUESTIONS }, (_, index) => {
          const step = index + 1;
          const isAnswered = step <= clamped;
          const isCurrent = !isComplete && step === current;

          return (
            <span
              key={step}
              data-testid={`progress-step-${step}`}
              data-state={
                isAnswered ? "answered" : isCurrent ? "current" : "upcoming"
              }
              className={[
                "h-1.5 flex-1 rounded-full transition-colors duration-300",
                isAnswered
                  ? "bg-primary"
                  : isCurrent
                    ? // Half-tone so the current step reads as in-progress
                      // rather than done — a full bar would overstate it.
                      "bg-primary/40"
                    : "bg-muted",
              ].join(" ")}
            />
          );
        })}
      </div>
    </div>
  );
}
